import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  Paperclip,
  Send,
  CheckCheck,
  ChevronLeft,
  Users,
  MessageSquare,
  X,
  ChevronLeft as PrevIcon,
  ChevronRight,
  Image,
} from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";
import { toast } from "react-toastify";
import storage from "../../assets/js/Global";
import {
  formatUtcToLocalTime,
  getLocalDateKey,
  getRelativeDateLabel,
} from "../../utils/timezone";

const ChatScreen = () => {
  const {
    chatList,
    getChatList,
    employeeList,
    getEmployeeList,
    createChatRoom,
    sendMessages,
    getMessages,
    heartBeat,
  } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const passedRoom = location.state?.room;
  const passedUser = location.state?.user;
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const currentUser = storage.get("user");

  const [selectedUser, setSelectedUser] = useState(passedUser);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sidebarTab, setSidebarTab] = useState("chats");
  const [selectedRoom, setSelectedRoom] = useState(passedRoom);
  const [search, setSearch] = useState({
    chats: "",
    contacts: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  // local override so the sidebar updates instantly on send (WhatsApp-style)
  const [chatListLocal, setChatListLocal] = useState(null);

  const openLightbox = (images, index) => setLightbox({ images, index });
  const closeLightbox = () => setLightbox(null);
  const lightboxPrev = () =>
    setLightbox((lb) => ({
      ...lb,
      index: (lb.index - 1 + lb.images.length) % lb.images.length,
    }));
  const lightboxNext = () =>
    setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length }));

  const fetchMessages = async () => {
    const res = await getMessages({ room_pid: selectedRoom.room_pid });
    if (res?.chat_details?.chat_room_message) {
      const formatted = res.chat_details.chat_room_message.map((msg) => ({
        id: msg.msg_pid,
        sender: msg.is_mine ? "me" : "other",
        text: msg.msg,
        time: msg.time,
        type: msg.msg_type === "img" ? "image" : "text",
        files: msg.file_path
          ? Array.isArray(msg.file_path)
            ? msg.file_path
            : [msg.file_path]
          : [],
        status: msg.status,
      }));
      setMessages(formatted);
    }
    heartBeat();
  };

  useEffect(() => {
    if (selectedRoom?.room_pid) fetchMessages();
  }, [selectedRoom]);

  // Clear the local override whenever a fresh list comes back from the server
  useEffect(() => {
    if (sidebarTab === "chats") {
      getChatList(search.chats).then(() => setChatListLocal(null));
    }
  }, [sidebarTab, search.chats]);

  useEffect(() => {
    if (sidebarTab === "contacts") {
      getEmployeeList(search.contacts);
    }
  }, [sidebarTab, search.contacts]);

  useEffect(() => {
    const savedRoom = storage.get("selectedRoom");
    const savedUser = storage.get("selectedUser");

    if (savedRoom && savedUser) {
      setSelectedRoom(savedRoom);
      setSelectedUser(savedUser);
    }
  }, []);

  useEffect(() => {
    const savedRoom = storage.get("selectedRoom");

    if (chatList?.length > 0 && !savedRoom && sidebarTab === "chats") {
      setSelectedUser(chatList[0].employee);
      setSelectedRoom(chatList[0]);
    }
  }, [chatList, sidebarTab]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") lightboxNext();
      else if (e.key === "ArrowLeft") lightboxPrev();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remainingSlots = 5 - selectedFiles.length;
    if (remainingSlots <= 0) {
      toast.error("You can only upload up to 5 images");
      return;
    }
    const allowedFiles = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.info(`Only ${remainingSlots} image(s) added (max 5 allowed)`);
    }
    const base64Files = await Promise.all(allowedFiles.map(toBase64));
    setSelectedFiles((prev) => [...prev, ...base64Files]);
    e.target.value = null;
  };

  const handleContactClick = async (user) => {
    let roomData;

    if (user.room_pid) {
      roomData = { room_pid: user.room_pid };
    } else {
      roomData = await createChatRoom(user.user_pid);
    }

    if (roomData) {
      setSelectedRoom(roomData);
      setSelectedUser(user);
      setMessages([]);

      storage.set("selectedRoom", roomData);
      storage.set("selectedUser", user);
    }
  };

  const sendMessage = async () => {
    const trimmed = inputText.trim();
    if (!selectedRoom?.room_pid) return;
    if (!trimmed && selectedFiles.length === 0) return;

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const filesToSend = [...selectedFiles];
    const optimisticId = Date.now();
    const lastMsg = filesToSend.length > 0 ? "Photo" : trimmed;

    // Optimistic message bubble in chat window
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        sender: "me",
        text: trimmed,
        time,
        type: filesToSend.length > 0 ? "image" : "text",
        files: filesToSend,
      },
    ]);

    // Optimistic sidebar update: move this conversation to the top instantly
    setChatListLocal((prev) => {
      const base = prev ?? chatList;
      const idx = base.findIndex((r) => r.room_pid === selectedRoom.room_pid);
      const entry =
        idx !== -1
          ? { ...base[idx], message: lastMsg, date_time: time }
          : {
              room_pid: selectedRoom.room_pid,
              employee: selectedUser,
              message: lastMsg,
              date_time: time,
            };
      const rest = idx !== -1 ? base.filter((_, i) => i !== idx) : base;
      return [entry, ...rest];
    });

    // Reset input
    setInputText("");
    setSelectedFiles([]);

    const result = await sendMessages({
      room_pid: selectedRoom.room_pid,
      msg: trimmed,
      msg_type: filesToSend.length > 0 ? "img" : "text",
      file_base64: filesToSend,
    });

    if (!result) {
      // Revert both message and sidebar on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setChatListLocal(null);
    } else {
      // Sync server list in background; clear local override when done
      getChatList(search.chats).then(() => setChatListLocal(null));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  const renderImageGrid = (files) => {
    const count = files.length;
    if (count === 0) return null;

    if (count === 1) {
      return (
        <img
          src={files[0]}
          alt="sent"
          className="msg-img-single"
          onClick={() => openLightbox(files, 0)}
        />
      );
    }

    const visible = files.slice(0, 4);
    const extra = count - 4;

    return (
      <div className={`msg-img-grid msg-img-grid-${Math.min(count, 4)}`}>
        {visible.map((file, i) => (
          <div
            key={i}
            className="msg-img-grid-item"
            onClick={() => openLightbox(files, i)}
          >
            <img src={file} alt={`img-${i}`} />
            {i === 3 && extra > 0 && (
              <div className="msg-img-more-overlay">+{extra} more</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Use local override while it exists so the sidebar reflects sends instantly
  const activeChatList = chatListLocal ?? chatList;
  const displayList = sidebarTab === "contacts" ? employeeList : activeChatList;

  // Inject WhatsApp-style date separators whenever the local date changes.
  const messagesWithSeparators = (() => {
    const out = [];
    let lastKey = null;
    messages.forEach((msg) => {
      const key = getLocalDateKey(msg.time);
      if (key && key !== lastKey) {
        out.push({
          type: "separator",
          id: `sep-${key}-${msg.id}`,
          label: getRelativeDateLabel(msg.time),
        });
        lastKey = key;
      }
      out.push({ type: "msg", msg });
    });
    return out;
  })();

  return (
    <div className="web-chat-container web-chat-container2">
      {lightbox && (
        <div className="chat-lightbox" onClick={closeLightbox}>
          <button className="chat-lightbox-close" onClick={closeLightbox}>
            <X size={22} />
          </button>

          {lightbox.images.length > 1 && (
            <div className="chat-lightbox-counter">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}

          {lightbox.images.length > 1 && (
            <button
              className="chat-lightbox-nav chat-lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                lightboxPrev();
              }}
            >
              <PrevIcon size={26} />
            </button>
          )}

          <img
            key={lightbox.index}
            src={lightbox.images[lightbox.index]}
            alt="preview"
            className="chat-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />

          {lightbox.images.length > 1 && (
            <button
              className="chat-lightbox-nav chat-lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                lightboxNext();
              }}
            >
              <ChevronRight size={26} />
            </button>
          )}

          {lightbox.images.length > 1 && (
            <div
              className="chat-lightbox-dots"
              onClick={(e) => e.stopPropagation()}
            >
              {lightbox.images.map((_, i) => (
                <span
                  key={i}
                  className={`chat-lightbox-dot ${i === lightbox.index ? "active" : ""}`}
                  onClick={() => setLightbox((lb) => ({ ...lb, index: i }))}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="chat-sidebar d-none d-md-flex">
        <div className="sidebar-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0 fw-extrabold ls-sm">Messages</h4>
            <div className="d-flex gap-2">
              <button
                className={`btn p-2 rounded-3 border-0 transition-all ${sidebarTab === "chats" ? "bg-primary text-white shadow-sm" : "bg-light text-muted"}`}
                onClick={() => setSidebarTab("chats")}
              >
                <MessageSquare size={18} />
              </button>
              <button
                className={`btn p-2 rounded-3 border-0 transition-all ${sidebarTab === "contacts" ? "bg-primary text-white shadow-sm" : "bg-light text-muted"}`}
                onClick={() => setSidebarTab("contacts")}
              >
                <Users size={18} />
              </button>
            </div>
          </div>
          <div className="form-group position-relative">
            <Search
              className="position-absolute text-muted"
              size={18}
              style={{
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              type="text"
              className="form-control border-0 bg-light rounded-pill fs-15 input-tags"
              style={{ paddingLeft: "42px", height: "48px" }}
              placeholder={
                sidebarTab === "chats"
                  ? "Search conversations..."
                  : "Search contacts..."
              }
              value={sidebarTab === "contacts" ? search.contacts : search.chats}
              onChange={(e) => {
                const value = e.target.value;

                setSearch((prev) => ({
                  ...prev,
                  [sidebarTab === "contacts" ? "contacts" : "chats"]: value,
                }));
              }}
            />
          </div>
        </div>

        <div className="chat-list-scroll">
          {displayList.length > 0 ? (
            displayList.map((item) => {
              const isContact = sidebarTab === "contacts";
              const user = isContact ? item : item.employee;
              const unread =
                item?.last_message_sender_id !== currentUser?.user_pid &&
                item.status === "unread";

              return (
                <div
                  key={isContact ? item.user_pid : item.room_pid}
                  className={`web-chat-item ${!isContact && selectedRoom?.room_pid === item.room_pid ? "active" : ""}`}
                  onClick={() => {
                    if (isContact) {
                      handleContactClick(user);
                    } else {
                      setChatListLocal((prev) => {
                        const base = prev ?? chatList;

                        return base.map((chat) =>
                          chat.room_pid === item.room_pid
                            ? {
                                ...chat,
                                unread_count: 0,
                                status: "read",
                              }
                            : chat,
                        );
                      });

                      setSelectedUser(user);
                      setSelectedRoom(item);
                      setMessages([]);

                      storage.set("selectedRoom", item);
                      storage.set("selectedUser", user);
                    }
                  }}
                >
                  <div className="position-relative">
                    <img
                      src={user?.profile_img || images.userAvatar}
                      alt="user"
                      className="chat-avatar-lg"
                      onError={(e) => {
                        e.currentTarget.src = images.userAvatar;
                      }}
                    />
                  </div>
                  <div className="chat-info-block ps-1">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="chat-user-name text-truncate">
                        {isContact
                          ? `${item.first_name} ${item.last_name}`
                          : `${user?.name}` || "Unknown"}
                      </span>
                      {!isContact && (
                        <span className="chat-time-stamp">
                          {item.date_time}
                        </span>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="chat-preview-text text-primary  text-truncate">
                        {isContact ? (
                          item.role
                        ) : ["photo", "img", "image"].includes(
                            item.message?.toLowerCase(),
                          ) ? (
                          <div className="d-flex align-items-center gap-1">
                            <Image size={13} />
                            <span>Photo</span>
                          </div>
                        ) : (
                          item.message || "No messages yet"
                        )}
                      </span>
                      {unread && (
                        <span className="msg-count-badge ms-2">
                          {item?.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted mt-3">
              {sidebarTab === "contacts" ? (
                search.contacts.trim() ? (
                  <>
                    No users found for "<strong>{search.contacts}</strong>"
                  </>
                ) : (
                  <>No users available</>
                )
              ) : search.chats.trim() ? (
                <>
                  No conversations found for "<strong>{search.chats}</strong>"
                </>
              ) : (
                <>No chats available</>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="chat-main-area">
        <header className="chat-main-header">
          <div className="active-user-info">
            <button
              className="btn p-0 me-1"
              onClick={() => navigate("/chat-list")}
            >
              <ChevronLeft size={24} />
            </button>
            <img
              src={selectedUser?.profile_img || images.userAvatar}
              alt="user"
              className="active-user-avatar"
              onError={(e) => {
                e.currentTarget.src = images.userAvatar;
              }}
            />
            <h6 className="mb-0 fs-17">
              {selectedUser
                ? `${selectedUser.first_name} ${selectedUser.last_name}`
                : "Select a chat"}
            </h6>
          </div>
        </header>

        <section className="web-chat-messages">
          {messages.length > 0 ? (
            messagesWithSeparators.map((item) =>
              item.type === "separator" ? (
                <div key={item.id} className="chat-date-separator">
                  <span>{item.label}</span>
                </div>
              ) : (
                <div
                  key={item.msg.id}
                  className={`msg-row ${item.msg.sender === "me" ? "me" : "other"}`}
                >
                  <div
                    className={`msg-bubble shadow-sm animate-bounce-in ${
                      item.msg.type === "image" ? "msg-bubble-img" : ""
                    }`}
                  >
                    {item.msg.type === "image" &&
                      item.msg.files?.length > 0 &&
                      renderImageGrid(item.msg.files)}

                    {item.msg.text && <p className="mb-0">{item.msg.text}</p>}

                    <div className="msg-meta d-flex align-items-center gap-1 justify-content-end">
                      <span className="msg-time">
                        {formatUtcToLocalTime(item.msg.time)}
                      </span>
                      {item.msg.sender === "me" && (
                        <CheckCheck
                          size={14}
                          className={`
                            mt-1
                            ${item.msg.status === "read" ? "text-success" : "opacity-75"}
                          `}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ),
            )
          ) : (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center opacity-50">
              <i className="fa-regular fa-message fs-1 mb-3"></i>
              <p className="fw-bold">No messages yet. Say hello!</p>
            </div>
          )}
          <div ref={bottomRef} />
        </section>

        <footer className="web-chat-input-area">
          {selectedFiles.length > 0 && (
            <div className="chat-file-preview-grid">
              {selectedFiles.map((file, index) => (
                <div key={index} className="chat-file-preview-item">
                  <img src={file} alt="preview" />
                  <button
                    className="chat-file-remove"
                    onClick={() =>
                      setSelectedFiles((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="web-input-wrapper">
            <div className="chat-btn-group">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp"
                multiple
                onChange={handleFileChange}
              />
              <button
                className="icon-action-btn p-1"
                onClick={() => fileInputRef.current.click()}
              >
                <Paperclip size={20} />
              </button>
            </div>
            <input
              type="text"
              className="web-chat-input"
              placeholder={
                selectedUser
                  ? `Message ${selectedUser.first_name || selectedUser.name || "User"}...`
                  : "Select a chat to start"
              }
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="web-send-btn shadow-md" onClick={sendMessage}>
              <Send size={20} />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ChatScreen;
